import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ReactComponent as ChevronDownIcon } from '@pkg/assets/icons/v2/chevron-down.svg';
import { ReactComponent as UserIcon } from '@pkg/assets/icons/v2/user.svg';
import { ButtonIcon } from '@pkg/components/Sections/Profile/Buttons';
import { TwitterPromptModal } from '@pkg/components/TwitterConnect/TwitterPromptModal';
import { YatSprite } from '@pkg/components/YatSprite';
import { useAppDispatch, useAppSelector, useLogout } from '@pkg/hooks';

import { YatPageVersion } from '@app/client/common/structs/yatPage/yat-page';
import config from '@app/config';
import { useOnClickOutside } from '@app/helpers';
import { setTwitterConnectIsOpen } from '@app/store/actions/dashboard';
import { convertYatPage } from '@app/store/actions/yat-page';
import { useEmojisStore } from '@app/store/zustand/emojis/useEmojisStore';

import DropdownMenuDivider from './DropdownMenuDivider';
import DropdownMenuHeading from './DropdownMenuHeading';
import DropdownMenuItem from './DropdownMenuItem';
import './UserNavigation.scss';

interface Props {
    variant?: 'v1' | 'v2';
    isCurrentUsersProfile?: boolean;
}

const YatDashboardTopBarUserNavigation = ({ variant = 'v1', isCurrentUsersProfile }: Props) => {
    const [translate] = useTranslation('common');
    const isAdmin = useAppSelector((state) => state.user.userClass?.isAdmin);
    const { logout } = useLogout();

    const { showConvertYatPage } = useAppSelector((state) => state.featureFlags.features);

    const userState = useAppSelector((rootState) => rootState.user);
    const currentEmoji = useEmojisStore((state) => state.currentEmoji);
    const yatPageVersion = useAppSelector((rootState) => rootState.yatPage.yatPageVersion);
    const [open, setOpen] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const toggleRef = useRef<HTMLButtonElement>(null);

    const dispatch = useAppDispatch();

    useOnClickOutside(containerRef, () => setOpen(false));
    const toggleOpen = useCallback(() => setOpen(!open), [open]);

    const signOut = () => {
        logout();
    };

    const handleToggleTwitter = useCallback(() => {
        dispatch(setTwitterConnectIsOpen(true));
    }, [dispatch]);

    const handleToggleKeyDown = useCallback(
        (e) => {
            if (e.key === 'Enter' || e.key === 'Space') {
                e.preventDefault();
                toggleOpen();
            }

            if (e.key === 'Escape') {
                e.preventDefault();
                setOpen(false);
            }
        },
        [toggleOpen],
    );

    const handleMenuKeyDown = useCallback(
        (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                setOpen(false);
                if (toggleRef.current) {
                    toggleRef.current.focus();
                }
            }
        },
        [toggleRef],
    );

    const checkFocusWithin = useCallback(() => {
        if (containerRef.current) {
            const focusWithin = (containerRef.current as HTMLDivElement).contains(document.activeElement);

            if (!focusWithin) {
                setOpen(false);
            }
        }
    }, [containerRef]);

    const convertCurrentYatPage = useCallback(() => {
        dispatch(convertYatPage(currentEmoji));
    }, [dispatch, currentEmoji]);

    useEffect(() => {
        document.addEventListener('focusin', checkFocusWithin);

        return () => {
            document.removeEventListener('focusin', checkFocusWithin);
        };
    }, [checkFocusWithin]);

    const classNames = useMemo(() => {
        const classNames: string[] = ['yat-dashboard-top-bar-user-navigation'];
        if (open) classNames.push('yat-dashboard-top-bar-user-navigation--is-open');
        if (variant === 'v2') classNames.push('yat-dashboard-top-bar-user-navigation--pods');
        return classNames.join(' ');
    }, [open, variant]);

    const sharedButtonProps = {
        ref: toggleRef,
        onClick: toggleOpen,
        onKeyDown: handleToggleKeyDown,
    };

    const hasTwitterData = userState?.userClass?.user?.twitter_data;

    return (
        <div ref={containerRef} className={classNames}>
            {!isCurrentUsersProfile && <TwitterPromptModal toggleOpen={handleToggleTwitter} forceClose={open} />}

            {variant === 'v1' && (
                <button
                    className="yat-dashboard-top-bar-user-navigation__toggle"
                    aria-controls="yat-dashboard-top-bar-user-navigation__list"
                    aria-haspopup="true"
                    aria-expanded={open}
                    {...sharedButtonProps}
                >
                    <span
                        id="yat-dashboard-top-bar-user-navigation__title"
                        className="yat-dashboard-top-bar-user-navigation__sr-title"
                    >
                        {translate('account')}
                    </span>
                    <UserIcon className="yat-dashboard-top-bar-user-navigation__user-icon" aria-hidden="true" />
                    <ChevronDownIcon
                        className="yat-dashboard-top-bar-user-navigation__chevron-icon"
                        aria-hidden="true"
                    />
                </button>
            )}
            {variant === 'v2' && (
                <ButtonIcon
                    size="small"
                    color="transparent"
                    aria-haspopup="true"
                    aria-expanded={open}
                    {...sharedButtonProps}
                >
                    <ChevronDownIcon />
                </ButtonIcon>
            )}
            <ul
                id="yat-dashboard-top-bar-user-navigation__list"
                className="yat-dashboard-top-bar-user-navigation__list v2-text-fluid-sm"
                role="menu"
                aria-labelledby="yat-dashboard-top-bar-user-navigation__title"
                onKeyDown={handleMenuKeyDown}
            >
                <DropdownMenuHeading>{translate('nav_security_settings')}</DropdownMenuHeading>
                <DropdownMenuItem>
                    <Link
                        to="/security-settings"
                        className="yat-dashboard-top-bar-user-navigation__item-link"
                        role="menuitem"
                    >
                        {translate('nav_two_factor_auth')}
                    </Link>
                </DropdownMenuItem>
                <DropdownMenuItem>
                    <Link
                        to="/change-password"
                        className="yat-dashboard-top-bar-user-navigation__item-link"
                        role="menuitem"
                    >
                        {translate('nav_change_password')}
                    </Link>
                </DropdownMenuItem>
                <DropdownMenuDivider />
                <DropdownMenuItem>
                    <a
                        href={config.supportLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="yat-dashboard-top-bar-user-navigation__item-link"
                        role="menuitem"
                    >
                        {translate('help_center')}
                    </a>
                </DropdownMenuItem>
                <DropdownMenuItem>
                    <button
                        onClick={handleToggleTwitter}
                        className="yat-dashboard-top-bar-user-navigation__item-button"
                        role="menuitem"
                    >
                        {hasTwitterData ? translate('twitter_manage') : translate('twitter_connect')}
                    </button>
                </DropdownMenuItem>
                <DropdownMenuItem>
                    <button
                        onClick={signOut}
                        className="yat-dashboard-top-bar-user-navigation__item-button"
                        role="menuitem"
                    >
                        {translate('sign_out')}
                    </button>
                </DropdownMenuItem>
                {isAdmin && (
                    <Fragment>
                        <DropdownMenuDivider />
                        <DropdownMenuItem>
                            <Link
                                to="/admin"
                                className="yat-dashboard-top-bar-user-navigation__item-link
                                yat-dashboard-top-bar-user-navigation__item-link--admin"
                            >
                                <YatSprite emojiID="🎩" />
                                <strong>Admin panel</strong>
                            </Link>
                        </DropdownMenuItem>

                        <DropdownMenuItem>
                            <Link
                                className="yat-dashboard-top-bar-user-navigation__item-link yat-dashboard-top-bar-user-navigation__item-link--admin"
                                to="/admin/users"
                            >
                                <YatSprite emojiID={'💃'} />
                                {translate('admin:manage_users')}
                            </Link>
                        </DropdownMenuItem>

                        {(isAdmin || showConvertYatPage) && yatPageVersion !== YatPageVersion.V2 && (
                            <DropdownMenuItem>
                                <Link
                                    to={'#'}
                                    onClick={convertCurrentYatPage}
                                    className="yat-dashboard-top-bar-user-navigation__item-link yat-dashboard-top-bar-user-navigation__item-link--admin"
                                >
                                    <YatSprite emojiID={'♻️'} />
                                    Convert to Yat Page 2.0
                                </Link>
                            </DropdownMenuItem>
                        )}
                    </Fragment>
                )}
            </ul>
        </div>
    );
};

export default YatDashboardTopBarUserNavigation;
